// Persistence Service - LocalStorage management with retry logic

import { DashboardStats, MinerStatus } from '../types';

// Cache keys
const CACHE_KEYS = {
  DASHBOARD_STATS: 'vulcan_dashboard_stats',
  MINERS_DATA: 'vulcan_miners_data',
  CRYPTO_PROGRESS: 'vulcan_crypto_progress',
  AVERAGE_HASHRATE: 'vulcan_average_hashrate',
  LAST_FETCH_TIME: 'vulcan_last_fetch_time',
  FETCH_STATUS: 'vulcan_fetch_status'
} as const;

// Cache TTL (Time To Live) in milliseconds
const CACHE_TTL = {
  DASHBOARD_STATS: 5 * 60 * 1000, // 5 minutes
  MINERS_DATA: 2 * 60 * 1000,     // 2 minutes
  CRYPTO_PROGRESS: 10 * 60 * 1000, // 10 minutes
  AVERAGE_HASHRATE: 3 * 60 * 1000  // 3 minutes
} as const;

// Retry configuration
const RETRY_CONFIG = {
  MAX_ATTEMPTS: 3,
  INITIAL_DELAY: 1000, // 1 second
  BACKOFF_MULTIPLIER: 2,
  MAX_DELAY: 10000 // 10 seconds
} as const;

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface FetchStatus {
  isFirstLoad: boolean;
  lastSuccessfulFetch: number;
  failedAttempts: number;
  nextRetryTime: number;
}

class PersistenceService {
  // Generic cache operations
  private setCache<T>(key: string, data: T, ttl: number): void {
    try {
      const cacheItem: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        ttl
      };
      localStorage.setItem(key, JSON.stringify(cacheItem));
      console.log(`💾 Cached data for key: ${key}`);
    } catch (error) {
      console.error(`❌ Failed to cache data for key ${key}:`, error);
    }
  }

  private getCache<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheItem: CacheItem<T> = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - cacheItem.timestamp > cacheItem.ttl) {
        console.log(`⏰ Cache expired for key: ${key}`);
        localStorage.removeItem(key);
        return null;
      }

      console.log(`✅ Cache hit for key: ${key}`);
      return cacheItem.data;
    } catch (error) {
      console.error(`❌ Failed to read cache for key ${key}:`, error);
      localStorage.removeItem(key);
      return null;
    }
  }

  private clearCache(key: string): void {
    localStorage.removeItem(key);
    console.log(`🗑️ Cleared cache for key: ${key}`);
  }

  // Fetch status management
  getFetchStatus(): FetchStatus {
    try {
      const cached = localStorage.getItem(CACHE_KEYS.FETCH_STATUS);
      if (!cached) {
        return {
          isFirstLoad: true,
          lastSuccessfulFetch: 0,
          failedAttempts: 0,
          nextRetryTime: 0
        };
      }
      return JSON.parse(cached);
    } catch (error) {
      console.error('❌ Failed to read fetch status:', error);
      return {
        isFirstLoad: true,
        lastSuccessfulFetch: 0,
        failedAttempts: 0,
        nextRetryTime: 0
      };
    }
  }

  setFetchStatus(status: Partial<FetchStatus>): void {
    try {
      const current = this.getFetchStatus();
      const updated = { ...current, ...status };
      localStorage.setItem(CACHE_KEYS.FETCH_STATUS, JSON.stringify(updated));
    } catch (error) {
      console.error('❌ Failed to save fetch status:', error);
    }
  }

  // Calculate next retry delay with exponential backoff
  calculateRetryDelay(attemptNumber: number): number {
    const delay = RETRY_CONFIG.INITIAL_DELAY * Math.pow(RETRY_CONFIG.BACKOFF_MULTIPLIER, attemptNumber - 1);
    return Math.min(delay, RETRY_CONFIG.MAX_DELAY);
  }

  // Check if we should retry based on current status
  shouldRetry(): boolean {
    const status = this.getFetchStatus();
    const now = Date.now();
    
    // Don't retry if we haven't failed yet
    if (status.failedAttempts === 0) return true;
    
    // Don't retry if we've exceeded max attempts
    if (status.failedAttempts >= RETRY_CONFIG.MAX_ATTEMPTS) {
      console.log(`🛑 Max retry attempts (${RETRY_CONFIG.MAX_ATTEMPTS}) reached`);
      return false;
    }
    
    // Don't retry if we're still in backoff period
    if (now < status.nextRetryTime) {
      const waitTime = Math.ceil((status.nextRetryTime - now) / 1000);
      console.log(`⏳ Waiting ${waitTime}s before next retry attempt`);
      return false;
    }
    
    return true;
  }

  // Mark fetch as successful
  markFetchSuccess(): void {
    this.setFetchStatus({
      isFirstLoad: false,
      lastSuccessfulFetch: Date.now(),
      failedAttempts: 0,
      nextRetryTime: 0
    });
  }

  // Mark fetch as failed and calculate next retry time
  markFetchFailure(): void {
    const status = this.getFetchStatus();
    const newAttemptCount = status.failedAttempts + 1;
    const retryDelay = this.calculateRetryDelay(newAttemptCount);
    
    this.setFetchStatus({
      failedAttempts: newAttemptCount,
      nextRetryTime: Date.now() + retryDelay
    });
    
    console.log(`❌ Fetch failed (attempt ${newAttemptCount}/${RETRY_CONFIG.MAX_ATTEMPTS}). Next retry in ${retryDelay}ms`);
  }

  // Dashboard Stats
  getDashboardStats(): DashboardStats | null {
    return this.getCache<DashboardStats>(CACHE_KEYS.DASHBOARD_STATS);
  }

  setDashboardStats(stats: DashboardStats): void {
    this.setCache(CACHE_KEYS.DASHBOARD_STATS, stats, CACHE_TTL.DASHBOARD_STATS);
  }

  // Miners Data
  getMinersData(): MinerStatus[] | null {
    return this.getCache<MinerStatus[]>(CACHE_KEYS.MINERS_DATA);
  }

  setMinersData(miners: MinerStatus[]): void {
    this.setCache(CACHE_KEYS.MINERS_DATA, miners, CACHE_TTL.MINERS_DATA);
  }

  // Crypto Progress
  getCryptoProgress(): any | null {
    return this.getCache(CACHE_KEYS.CRYPTO_PROGRESS);
  }

  setCryptoProgress(data: any): void {
    this.setCache(CACHE_KEYS.CRYPTO_PROGRESS, data, CACHE_TTL.CRYPTO_PROGRESS);
  }

  // Average Hashrate
  getAverageHashrate(): any | null {
    return this.getCache(CACHE_KEYS.AVERAGE_HASHRATE);
  }

  setAverageHashrate(data: any): void {
    this.setCache(CACHE_KEYS.AVERAGE_HASHRATE, data, CACHE_TTL.AVERAGE_HASHRATE);
  }

  // Check if any cache needs refresh
  needsRefresh(): boolean {
    const status = this.getFetchStatus();
    
    // Always refresh on first load
    if (status.isFirstLoad) return true;
    
    // Check if any cache is expired
    const dashboardStats = this.getDashboardStats();
    const minersData = this.getMinersData();
    const cryptoProgress = this.getCryptoProgress();
    const averageHashrate = this.getAverageHashrate();
    
    return !dashboardStats || !minersData || !cryptoProgress || !averageHashrate;
  }

  // Clear all cache
  clearAllCache(): void {
    Object.values(CACHE_KEYS).forEach(key => {
      this.clearCache(key);
    });
    console.log('🗑️ All cache cleared');
  }

  // Get cache info for debugging
  getCacheInfo(): Record<string, any> {
    const info: Record<string, any> = {};
    
    Object.entries(CACHE_KEYS).forEach(([name, key]) => {
      const cached = localStorage.getItem(key);
      if (cached) {
        try {
          const cacheItem = JSON.parse(cached);
          const age = Date.now() - cacheItem.timestamp;
          const remaining = cacheItem.ttl - age;
          
          info[name] = {
            hasData: true,
            age: Math.round(age / 1000),
            remaining: Math.round(remaining / 1000),
            expired: remaining <= 0,
            lastUpdate: new Date(cacheItem.timestamp).toLocaleTimeString()
          };
        } catch (error) {
          info[name] = { hasData: false, error: 'Parse error' };
        }
      } else {
        info[name] = { hasData: false };
      }
    });
    
    return info;
  }

  // Get overall data freshness status
  getDataFreshness(): {
    isUsingCache: boolean;
    oldestDataAge: number;
    hasStaleData: boolean;
    lastSuccessfulFetch: number;
    dataQuality: 'fresh' | 'cached' | 'stale' | 'missing';
  } {
    const fetchStatus = this.getFetchStatus();
    const cacheInfo = this.getCacheInfo();
    
    const hasAnyData = Object.values(cacheInfo).some((info: any) => info.hasData);
    const hasStaleData = Object.values(cacheInfo).some((info: any) => info.hasData && info.expired);
    const oldestDataAge = Math.max(...Object.values(cacheInfo)
      .filter((info: any) => info.hasData)
      .map((info: any) => info.age));
    
    let dataQuality: 'fresh' | 'cached' | 'stale' | 'missing' = 'missing';
    
    if (!hasAnyData) {
      dataQuality = 'missing';
    } else if (fetchStatus.lastSuccessfulFetch > Date.now() - 60000) { // Less than 1 minute old
      dataQuality = 'fresh';
    } else if (hasStaleData) {
      dataQuality = 'stale';
    } else {
      dataQuality = 'cached';
    }
    
    return {
      isUsingCache: hasAnyData && fetchStatus.lastSuccessfulFetch < Date.now() - 30000,
      oldestDataAge: isNaN(oldestDataAge) ? 0 : oldestDataAge,
      hasStaleData,
      lastSuccessfulFetch: fetchStatus.lastSuccessfulFetch,
      dataQuality
    };
  }
}

// Export singleton instance
export const persistenceService = new PersistenceService();
export default persistenceService;